import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Card, CardContent, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  ToggleButton, ToggleButtonGroup, Alert as MuiAlert, Snackbar, Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useCommodities, useMarkets, useAlerts, useLocalPrices, useCategories } from '../../services/queries';
import { createAlert, updateAlert, deleteAlert } from '../../services/account';
import { limitsFor, atLimit, usageLabel, isPlanLimit } from '../../utils/planLimits';
import { PageHeader, LoadingSkeleton, EmptyState, FilterSelect, ConfirmDialog, UpdatedStamp } from '../../components/common';
import { useRecipes } from '../../theme/recipes';
import { evaluateAlerts } from '../../utils/alertTriggers';
import { activeCommodities, isCommodityActive } from '../../utils/catalogStatus';

function Alerts() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: commodities = [] } = useCommodities();
  const { data: markets = [] } = useMarkets();
  const { data: alerts = [], isLoading } = useAlerts();

  // Only ACTIVE alerts count against the plan, matching the server: pausing
  // one frees the slot, so the count here has to agree or the page would
  // offer a create that the API then refuses.
  const planLimits = limitsFor(user);
  const activeAlertCount = alerts.filter((a) => a.status === 'active').length;
  const alertsFull = atLimit(activeAlertCount, planLimits.maxAlerts);
  const planUsage = usageLabel(activeAlertCount, planLimits.maxAlerts, 'alerts');
  const { data: localPrices = [] } = useLocalPrices();
  const { data: commodityCategories = [] } = useCategories();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    commodityId: '',
    marketId: 'all',
    direction: 'below',
    threshold: '',
    unit: 'bag',
  });

  // Compact variant of the primary CTA recipe for header/dialog buttons
  const ctaSx = { ...recipes.primaryBtnSx, px: 2.5, py: 0.9, fontSize: '0.875rem', textTransform: 'none' };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['alerts'] });

  const openCreate = () => {
    setEditing(null);
    setForm({ commodityId: pickerCommodities[0]?.id || '', marketId: 'all', direction: 'below', threshold: '', unit: 'bag' });
    setDialogOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ commodityId: a.commodityId, marketId: a.marketId, direction: a.direction, threshold: String(a.threshold), unit: a.unit });
    setDialogOpen(true);
  };
  const save = async () => {
    const threshold = Number(form.threshold);
    if (!threshold || threshold <= 0) { setToast({ severity: 'error', msg: 'Enter a valid threshold.' }); return; }
    const payload = { commodityId: form.commodityId, marketId: form.marketId, direction: form.direction, threshold, unit: form.unit };
    try {
      if (editing) await updateAlert(editing, payload);
      else await createAlert(payload);
      refresh();
      setDialogOpen(false);
      setToast({ severity: 'success', msg: editing ? 'Alert updated.' : 'Alert saved.' });
    } catch (e) {
      setToast({
        severity: isPlanLimit(e) ? 'warning' : 'error',
        msg: e?.message || 'Could not save alert.',
      });
    }
  };
  const remove = async (id) => {
    try {
      await deleteAlert(id);
      refresh();
      setToast({ severity: 'info', msg: 'Alert removed.' });
    } catch (e) {
      setToast({ severity: 'error', msg: e?.message || 'Could not remove alert.' });
    }
  };
  const toggleStatus = async (id) => {
    const a = alerts.find((x) => x.id === id);
    if (!a) return;
    try {
      await updateAlert(id, { status: a.status === 'active' ? 'paused' : 'active' });
      refresh();
    } catch (e) {
      setToast({
        severity: isPlanLimit(e) ? 'warning' : 'error',
        msg: e?.message || 'Could not update alert.',
      });
    }
  };

  const commodityName = (id) => commodities.find((c) => c.id === id)?.name || id;

  // Picker offers ACTIVE commodities; when editing an alert that references a
  // retired one, that commodity is appended (labeled) so the alert stays
  // editable instead of showing an empty select.
  const pickerCommodities = useMemo(
    () => activeCommodities(commodities, commodityCategories),
    [commodities, commodityCategories]
  );
  const commodityOptions = useMemo(() => {
    const opts = pickerCommodities.map((c) => ({ value: c.id, label: c.name }));
    if (form.commodityId && !pickerCommodities.some((c) => c.id === form.commodityId)) {
      const c = commodities.find((x) => x.id === form.commodityId);
      if (c) opts.push({ value: c.id, label: `${c.name} (retired)` });
    }
    return opts;
  }, [pickerCommodities, commodities, form.commodityId]);
  const marketName = (id) => id === 'all' ? 'All markets' : (markets.find((m) => m.id === id)?.name || id);

  const activeCount = useMemo(() => alerts.filter((a) => a.status === 'active').length, [alerts]);
  const confirmTarget = confirmId ? alerts.find((a) => a.id === confirmId) : null;

  // Alerts whose threshold the market has crossed (same evaluation the
  // header bell uses), keyed by alert id for the TRIGGERED chip.
  const triggeredById = useMemo(
    () => new Map(evaluateAlerts(alerts, localPrices).map((t) => [t.alert.id, t])),
    [alerts, localPrices]
  );

  // Latest observed price for the dialog's commodity/market/unit selection,
  // shown next to the threshold so the user sets an informed number. When
  // "All markets" is chosen, it averages the newest collection date's records.
  const currentPrice = useMemo(() => {
    if (!form.commodityId) return null;
    const rows = localPrices.filter((p) =>
      p.commodityId === form.commodityId && (form.marketId === 'all' || p.marketId === form.marketId)
    );
    if (rows.length === 0) return null;
    let latest = rows[0].date;
    rows.forEach((p) => { if (p.date > latest) latest = p.date; });
    const dayRows = rows.filter((p) => p.date === latest);
    const vals = dayRows.map((p) => (form.unit === 'kg' ? p.pricePerKg : p.price)).filter((v) => v != null);
    if (vals.length === 0) return null;
    return {
      value: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length),
      date: latest,
      samples: dayRows.length,
    };
  }, [localPrices, form.commodityId, form.marketId, form.unit]);

  if (isLoading) {
    return <Box sx={{ p: 1 }}><LoadingSkeleton variant="page" /></Box>;
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Premium"
        title="Price Alerts"
        subtitle={
          <>
            Get notified when commodity prices cross your thresholds. Currently{' '}
            <Box component="span" sx={{ fontWeight: 700, color: palette.accent }}>{activeCount}</Box>{' '}
            active alert{activeCount === 1 ? '' : 's'}.
          </>
        }
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Price Alerts' }
        ]}
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {planUsage && (
              <Typography sx={{ fontSize: '0.78rem', color: alertsFull ? palette.warning : palette.textMuted }}>
                {planUsage}
              </Typography>
            )}
            {alertsFull ? (
              <Button
                component={RouterLink}
                to="/app/subscription"
                variant="contained"
                disableElevation
                startIcon={<Icon icon="ph:arrow-up-right" width={16} height={16} />}
                sx={ctaSx}
              >
                Upgrade for more
              </Button>
            ) : (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Icon icon="ph:plus" width={16} height={16} />}
                onClick={openCreate}
                sx={ctaSx}
              >
                Create alert
              </Button>
            )}
          </Box>
        }
      />

      {/* Delivery notice */}
      <MuiAlert
        severity="info"
        icon={<Icon icon="ph:envelope-simple" width={20} height={20} />}
        sx={{ mb: 3, bgcolor: palette.infoBg, border: `1px solid ${palette.info}30`, color: palette.text, '& .MuiAlert-icon': { color: palette.info } }}
      >
        Alerts are emailed to <Box component="span" sx={{ fontWeight: 700 }}>{user.email}</Box>, at most once a day per alert. Turn them off any time in Settings.
      </MuiAlert>

      {/* Empty state / list */}
      {alerts.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icon icon="ph:bell-ringing" width={32} height={32} />}
            title="No alerts yet"
            description="Create your first alert to be notified when a commodity moves past a threshold you care about."
            actionLabel="Create alert"
            onAction={openCreate}
          />
        </Card>
      ) : (
        <Grid container spacing={2}>
          {alerts.map((a) => (
            <Grid item xs={12} md={6} key={a.id}>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                          <Chip label={a.status === 'active' ? 'ACTIVE' : 'PAUSED'} size="small"
                            sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', bgcolor: a.status === 'active' ? palette.accentBg : palette.surfaceHover, color: a.status === 'active' ? palette.accent : palette.textMuted }} />
                          {triggeredById.has(a.id) && (
                            <Chip
                              icon={<Icon icon={a.direction === 'below' ? 'ph:trend-down' : 'ph:trend-up'} width={11} height={11} />}
                              label={`TRIGGERED · NOW ₦${triggeredById.get(a.id).observedPrice.toLocaleString()}`}
                              size="small"
                              sx={{
                                height: 18, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.06em',
                                fontFamily: MONO,
                                bgcolor: a.direction === 'below' ? palette.dangerBg : palette.accentBg,
                                color: a.direction === 'below' ? palette.danger : palette.accent,
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: palette.text }}>
                            {commodityName(a.commodityId)}
                          </Typography>
                          {(() => {
                            const c = commodities.find((x) => x.id === a.commodityId);
                            return c && !isCommodityActive(c, commodityCategories) ? <Chip label="RETIRED" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.55rem', fontWeight: 700, letterSpacing: '0.06em', bgcolor: palette.surfaceHover, color: palette.textMuted }} /> : null;
                          })()}
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary, mb: 1 }}>
                          {marketName(a.marketId)} &middot; {a.direction === 'below' ? 'drops below' : 'rises above'}{' '}
                          <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, color: a.direction === 'below' ? palette.danger : palette.accent }}>
                            ₦{a.threshold.toLocaleString()}
                          </Box>{' '}
                          per {a.unit === 'kg' ? 'kg' : 'bag'}
                        </Typography>
                        <UpdatedStamp date={a.createdAt} prefix="Created" sx={{ fontSize: '0.68rem' }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          aria-label={a.status === 'active' ? 'Pause alert' : 'Resume alert'}
                          onClick={() => toggleStatus(a.id)}
                          sx={{ color: palette.textMuted }}
                        >
                          <Icon icon={a.status === 'active' ? 'ph:pause' : 'ph:play'} width={16} height={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Edit alert"
                          onClick={() => openEdit(a)}
                          sx={{ color: palette.textMuted }}
                        >
                          <Icon icon="ph:pencil-simple" width={16} height={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete alert"
                          onClick={() => setConfirmId(a.id)}
                          sx={{ color: palette.danger }}
                        >
                          <Icon icon="ph:trash" width={16} height={16} />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 520 } }}
      >
        <DialogTitle sx={{ color: palette.text, fontWeight: 700 }}>
          {editing ? 'Edit alert' : 'Create price alert'}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: palette.divider }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FilterSelect
              label="Commodity"
              value={form.commodityId}
              onChange={(v) => setForm({ ...form, commodityId: v })}
              options={commodityOptions}
            />
            <FilterSelect
              label="Market"
              value={form.marketId}
              onChange={(v) => setForm({ ...form, marketId: v })}
              options={[
                { value: 'all', label: 'All markets (any)' },
                ...markets.map((m) => ({ value: m.id, label: `${m.name} (${m.state})` })),
              ]}
            />
            {/* Current price for the selection - context for the threshold */}
            <Box
              sx={{
                px: 1.5,
                py: 1.1,
                borderRadius: '10px',
                bgcolor: palette.surface,
                border: `1px solid ${palette.cardBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: palette.textMuted }}>
                  Current price
                </Typography>
                {currentPrice ? (
                  <>
                    <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: '1rem', color: palette.text }}>
                      ₦{currentPrice.value.toLocaleString()}
                      <Typography component="span" sx={{ fontFamily: MONO, fontSize: '0.72rem', color: palette.textMuted, ml: 0.5 }}>
                        per {form.unit === 'kg' ? 'kg' : 'bag'}
                        {form.marketId === 'all' && currentPrice.samples > 1 ? ` · avg of ${currentPrice.samples} markets` : ''}
                      </Typography>
                    </Typography>
                    <UpdatedStamp date={currentPrice.date} />
                  </>
                ) : (
                  <Typography sx={{ fontSize: '0.8rem', color: palette.textMuted }}>
                    No recent price data for this selection
                  </Typography>
                )}
              </Box>
              {currentPrice && (
                <Button
                  size="small"
                  onClick={() => setForm({ ...form, threshold: String(currentPrice.value) })}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: palette.accent, whiteSpace: 'nowrap', '&:hover': { bgcolor: palette.accentBg } }}
                >
                  Use as threshold
                </Button>
              )}
            </Box>

            <ToggleButtonGroup
              value={form.direction}
              exclusive
              onChange={(_, v) => v && setForm({ ...form, direction: v })}
              fullWidth
              size="small"
              aria-label="Alert direction"
            >
              <ToggleButton value="below" sx={{ textTransform: 'none', color: palette.text, borderColor: palette.cardBorder, '&.Mui-selected': { bgcolor: palette.dangerBg, color: palette.danger, borderColor: palette.danger } }}>
                <Icon icon="ph:trend-down" width={16} height={16} style={{ marginRight: 8 }} /> Drops below
              </ToggleButton>
              <ToggleButton value="above" sx={{ textTransform: 'none', color: palette.text, borderColor: palette.cardBorder, '&.Mui-selected': { bgcolor: palette.accentBg, color: palette.accent, borderColor: palette.accent } }}>
                <Icon icon="ph:trend-up" width={16} height={16} style={{ marginRight: 8 }} /> Rises above
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Threshold (₦)"
                type="number"
                size="small"
                fullWidth
                value={form.threshold}
                onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                sx={recipes.searchSx}
                InputLabelProps={{ sx: recipes.labelSx }}
                InputProps={{ sx: { fontFamily: MONO } }}
              />
              <FilterSelect
                label="Unit"
                value={form.unit}
                onChange={(v) => setForm({ ...form, unit: v })}
                options={[
                  { value: 'bag', label: 'per bag' },
                  { value: 'kg', label: 'per kg' },
                ]}
                fullWidth={false}
                minWidth={120}
              />
            </Box>
            <Divider sx={{ borderColor: palette.divider }} />
            <Typography sx={{ fontSize: '0.78rem', color: palette.textMuted, lineHeight: 1.5 }}>
              Preview: <Box component="span" sx={{ color: palette.text, fontWeight: 600 }}>
                {commodityName(form.commodityId)} in {marketName(form.marketId)} {form.direction === 'below' ? 'drops below' : 'rises above'}{' '}
                ₦{Number(form.threshold || 0).toLocaleString()} per {form.unit === 'kg' ? 'kg' : 'bag'}
              </Box>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={save} variant="contained" disableElevation sx={ctaSx}>
            {editing ? 'Save changes' : 'Create alert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => { remove(confirmId); setConfirmId(null); }}
        title="Delete this alert?"
        description={
          confirmTarget
            ? `The ${commodityName(confirmTarget.commodityId)} alert (${marketName(confirmTarget.marketId)}) will be permanently removed.`
            : 'This alert will be permanently removed.'
        }
        confirmLabel="Delete alert"
        tone="danger"
        icon="ph:trash"
      />

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast && <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</MuiAlert>}
      </Snackbar>
    </Box>
  );
}

export default Alerts;
